import { AccountTypeEnum } from 'constants/northCapital'
import { OnboardingTypeEnum } from 'hooks/useRiseUpAPIV1'
import { InvestingPersonEnum, RetirementAccountEnum } from 'interfaces'
import _ from 'lodash'
import queryString from 'query-string'
import { FieldError } from 'react-hook-form'

export const getListOfYears = (numberOfYears: number) => {
  const currentYear = new Date().getFullYear()
  const range = (start: any, stop: any, step: any) =>
    Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step)

  return range(currentYear, currentYear + numberOfYears, +1)
}

export const isAmtDivisibleBySharePrice = (amount: string | number, sharePrice: number) => {
  let value
  if (typeof amount === 'string') {
    value = amount.replace(/[$,]/g, '')
  } else if (typeof amount === 'number') {
    value = amount
  }

  return Number(value) % sharePrice === 0
}

export const validatePostalCode = (value: string) => {
  return /^\d{5}(?:[-\s]\d{4})?$/.test(value)
}

export const maskSSN = (input: string): string => {
  let count = 0
  return input.replace(/\d/g, (match) => {
    count++
    return count <= 5 ? '*' : match
  })
}

export const maskNumbersExceptLastFour = (input: string): string => {
  const numbers = input.match(/\d/g)
  const totalNumbers = numbers ? numbers.length : 0
  let count = 0
  return input.replace(/\d/g, () => {
    count++
    return count <= totalNumbers - 4 ? '*' : input[count - 1]
  })
}

export const isFieldError = (obj: any): obj is FieldError => {
  return !!obj && typeof obj.type === 'string'
}

export const getAccountType = (investingPerson: InvestingPersonEnum): AccountTypeEnum => {
  switch (investingPerson) {
    case InvestingPersonEnum.FOR_MYSELF:
      return AccountTypeEnum.FOR_MYSELF
    case InvestingPersonEnum.JOINTLY:
      return AccountTypeEnum.JOINTLY
    case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      return AccountTypeEnum.IN_BEHALF_OF_AN_ENTITY
    case InvestingPersonEnum.RETIREMENT:
      return AccountTypeEnum.RETIREMENT
    default:
      return AccountTypeEnum.FOR_MYSELF
  }
}

export const formatNumber = (value: number, locale: string = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const returnFormErrorString = (errors: any): any => {
  for (const [key, value] of Object.entries(errors)) {
    if (errors[key]?.type === 'required') {
      return `${_.capitalize(_.startCase(key))} is required.`
    } else {
      return `${_.capitalize(_.startCase(key))} is invalid format.`
    }
  }
}

export const getEntityType = (type: string) => {
  switch (type) {
    case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      return 'Company'
    case RetirementAccountEnum.RETIREMENT_ACCOUNT:
      return 'Retirement Account'
    case RetirementAccountEnum.TRUST_ACCOUNT:
      return 'Trust'
    default:
      return 'Entity'
  }
}

export const getOnboardingType = (investingPerson: string) => {
  switch (investingPerson) {
    case InvestingPersonEnum.FOR_MYSELF:
      return OnboardingTypeEnum.INDIVIDUAL
    case InvestingPersonEnum.JOINTLY:
      return OnboardingTypeEnum.JOINT
    case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      return OnboardingTypeEnum.COMPANY
    case RetirementAccountEnum.RETIREMENT_ACCOUNT:
      return OnboardingTypeEnum.IRA
    case RetirementAccountEnum.TRUST_ACCOUNT:
      return OnboardingTypeEnum.TRUST
    default:
      return OnboardingTypeEnum.INDIVIDUAL
  }
}

export const getInvestorAccountType = (type: string) => {
  switch (type) {
    case InvestingPersonEnum.FOR_MYSELF:
      return 'Individual'
    case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      return 'Entity'
    case RetirementAccountEnum.RETIREMENT_ACCOUNT:
      return 'IRA'
    case RetirementAccountEnum.TRUST_ACCOUNT:
      return 'Entity'
    case 'survivorship':
      return 'JTWROS'
    case 'common':
      return 'TIC'
    case 'entirety':
      return 'Joint'
    default:
      return ''
  }
}

export const validatePhone = (value: string) => {
  return !value.includes('_')
}
export const validateUSPhone = (value: string) => {
  return /^(\+1)?\d{10}$/.test(value)
}

/**
 * Gets a URL with a modified step parameter using provided current parameters
 * @param pathname - The current pathname
 * @param currentParams - The current query parameters object
 * @param newStepValue - The new step value to set
 * @returns The modified URL
 */
export const getModifiedUrlWithStep = (
  pathname: string,
  currentParams: Record<string, any>,
  newStepValue: string,
): string => {
  // Update the step parameter
  const updatedParams = {
    ...currentParams,
    step: newStepValue,
  }

  // Stringify the updated parameters
  const updatedSearch = queryString.stringify(updatedParams)

  // Return the combined URL
  return `${pathname}?${updatedSearch}`
}
